import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { cortisolQuiz } from "./cortisol";
import { countReportedSigns } from "./cortisol-profile";
import { deriveCortisolStoryProfile } from "./cortisol-story-profile";

/**
 * CORTISOL-STORY -- an "interactive advertorial" (paged quiz-torial) for the
 * cortisol angle.
 *
 * Dr. Latt Mansor's `/pages/cortisol-adv` advertorial argument runs the
 * spine, but at each beat where the advertorial TELLS the reader "here's
 * what's happening to you", this asks instead. The reader self-diagnoses
 * their way through the argument and lands on the same locked T-suppression
 * verdict the plain `cortisol` quiz ships.
 *
 * 7 editorial beats + 10 self-checks = 17 screens, then the reused tail
 * (result -> projection -> offer -> applying -> PDP).
 *
 * Design + audit: docs/specs/2026-07-10-cortisol-story-interactive-advertorial-design.md
 * Approved copy:  docs/cortisol-story-flow.html (the tap-through IS the copy source)
 * Build plan:     docs/plans/2026-08-03-cortisol-story.md
 *
 * DERIVER CONTRACT. The 10 self-check question ids AND their option values
 * are byte-identical to `quizzes/cortisol.ts`, which is why this quiz reuses
 * `deriveCortisolProfile` and the classifier directly rather than cloning
 * them. Labels and prompts may differ (they are not the contract); values may
 * not. Editing an option value here without editing cortisol.ts silently
 * desyncs the verdict.
 *
 * Like quiz-v6 and cortisol, there is no GLP-1 screener, so `glp1-user` is not
 * reachable from the classifier.
 */

const QUESTION_ORDER = [
  "hook",
  "age",
  "authority",
  "caveman",
  "pivot",
  "puffy-face",
  "wake-pattern",
  "tension",
  "energy-3pm",
  "bridge-absolution",
  "body-composition",
  "sugar-cravings",
  "brain-fog",
  "libido",
  "when-started",
  "bridge-mechanism",
  "analyzing",
] as const;

/**
 * Byline for the editorial beats. Name, credential, and likeness are already
 * published by Mars Men in two places (the cortisol advertorial body and the
 * Medical Advisory Board card on the PDP), so this is reuse of live
 * attribution, not an invented persona. Operator sign-off for THIS surface is
 * tracked in spec §12.
 *
 * OPEN ITEM (final copy, 2026-09-02). The Columbia biotechnology master's is
 * NEW here: spec §12 documented the Oxford PhD, the special-ops / Olympic /
 * research-institution bio, and the likeness as already-live on
 * `/pages/cortisol-adv`, but not this. It rides the same operator sign-off,
 * and it must be sourced (not assumed) before this page takes traffic -- these
 * are a real person's credentials on a commercial page.
 *
 * `avatar` is intentionally omitted: the photo (`dr1.webp`) has not been
 * pulled into `/static/images/` yet. The byline renders name + credentials
 * without it, so the quiz ships correctly either way.
 */
const MANSOR = {
  name: "Dr. Latt Mansor",
  credentials:
    "PhD, M.S. · Physiology, Anatomy & Genetics, University of Oxford · Biotechnology, Columbia University",
} as const;

const questions: Question[] = [
  {
    id: "hook",
    type: "editorial",
    byline: { ...MANSOR, read: "3-min read" },
    // Headline held VERBATIM from the live advertorial so the ad-to-page read
    // is 1:1 for cold traffic. Body para 1 carries the canon avatar language
    // (softer / puffier / more tired) that "overweight" alone misses; para 4
    // is the flow's only forward-looking payoff promise and stands in for the
    // absent Sultanic POSITIVE beat (spec §4 design note).
    //
    // Para 2 (the normal-labs beat) and para 3 (pre-handling "cortisol is a
    // buzzword") are the final-copy additions: the objection a cold reader
    // brings to a cortisol claim is disqualification-by-bloodwork followed by
    // fatigue with the word itself, and both now get answered before the ask.
    heading: "The #1 thing keeping men overweight in 2026 isn't food. It's cortisol.",
    body: [
      "You're eating clean. You're training hard. And every year a softer, puffier, more tired man shows up in the mirror wearing your face.",
      "Then your labs come back normal and your doctor tells you to come back in a year. So you wait a year. Whatever is doing this doesn't wait with you.",
      "I know how \"it's cortisol\" sounds. It's been blamed for everything from belly fat to bad moods, which is exactly why most men have stopped hearing it. Stay with me anyway. The men who tune it out are the ones it keeps working on.",
      "Give me 3 minutes. By the end you'll know exactly what's been beating you, and how far it's gotten.",
    ],
    cta: "Show me what's beating me",
  },
  {
    id: "age",
    type: "single",
    // Position 2, not 3 (audit F3): pulls the first interaction one screen
    // earlier, and restores the advertorial's David -> "here's what I
    // explained to David" -> caveman adjacency.
    prompt: "First, so I can calibrate this to you: how old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40–49 years old" },
      { value: "30-39", label: "30–39 years old" },
      { value: "20-29", label: "20–29 years old" },
    ],
  },
  {
    id: "authority",
    type: "editorial",
    byline: { ...MANSOR, read: "continued" },
    eyebrow: "Why you can trust this",
    heading: "I study how the male body makes testosterone, and what stops it.",
    // David's specifics (46, eight months, 6 days, 2,100 cal, 7+ hrs) are
    // lifted from the advertorial and deliberately NOT smoothed down --
    // specificity is what makes a reader mutter "that's me". The final copy
    // adds the shoulders-shrinking detail and the "doing the work of a
    // disciplined man" line, which move David from a weight story to a
    // recomposition story, and end the beat on an open loop ("I pulled his
    // bloodwork instead") that the CTA asks outright.
    body: [
      "I hold a PhD in Physiology, Anatomy and Genetics from the University of Oxford, where my research focused on metabolism, and a Master's in Biotechnology from Columbia. I've spent my career as a peak performance consultant working with elite special operations units, Olympic athletes, and top research institutions. I've seen every optimization protocol out there, and I've seen which ones quietly fail.",
      "Take David, 46. Eight months of training 6 days a week, 2,100 clean calories a day, 7-plus hours of sleep, all of it tracked. His belly grew anyway. And while it grew, his shoulders got smaller.",
      "He didn't come to me about his weight. He came to me because he was doing the work of a disciplined man and getting back the body of one who'd given up.",
      "His doctor called it age. I pulled his bloodwork instead.",
    ],
    pullQuote: "David wasn't losing to his diet. He was losing to his own bloodstream.",
    cta: "Then what was it?",
  },
  {
    id: "caveman",
    type: "editorial",
    eyebrow: "The mechanism",
    heading: "Your body was built for a world that no longer exists.",
    // The 200,000-year and 16-hour figures are advertorial-sourced rhetoric,
    // held verbatim from the live page; they are NOT from
    // docs/t-suppression-research.md and are flagged for sign-off (spec §12).
    //
    // Para 4 is the final-copy addition and the beat that earns the UMP: it
    // turns "here is a mechanism" into "here is why every fix you already
    // tried made it worse", which is the objection a disciplined reader is
    // holding by this point.
    body: [
      "For 200,000 years this worked perfectly. A predator appears. Cortisol spikes. Your body shuts down everything that isn't survival, testosterone included. The threat passes. Cortisol drops. Testosterone surges back. The whole thing took about 90 seconds, and then it reset.",
      "That system was built for threats that **ended**. Yours don't. The mortgage doesn't end. The inbox doesn't end. The tuition doesn't end. Your nervous system can't tell the difference between a lion and a Tuesday, so it never sounds the all-clear.",
      "So the cortisol never drops. Not for 90 seconds the way it was built to. For 16 hours a day, year after year. And every system your body shut down to survive the emergency stays shut down, **including the one that makes you a man**.",
      "And every fix you've been handed feeds it. Eat less and cortisol climbs. Train harder and cortisol climbs. Fat burners work by spiking the exact hormone already beating you. Twenty years of advice, and almost all of it pours fuel on the fire.",
    ],
    // Canon mechanism sentence (docs/product-brief.md). The single
    // load-bearing line of the flow, so it gets its own beat.
    pullQuote: "Your body wants to make testosterone. It's being blocked.",
    // No image: the caveman illustration is operator-supplied and has no
    // production asset yet (spec §12). The screen reads correctly without it,
    // but the final copy runs harder on the ancestral frame than the draft
    // did, so the illustration now has a real job here -- worth sourcing.
    cta: "How do I know if that's me?",
  },
  {
    id: "pivot",
    type: "editorial",
    eyebrow: "Here's the part that matters",
    heading: "High cortisol leaves fingerprints. Let's look for yours.",
    body: [
      "Cortisol that never switches off leaves marks, and it leaves them everywhere. On your face. On your sleep. On your temper. On what you want and whether you still want it.",
      // Audit F2: this line PLANTS the eight-signs open loop. The parent quiz
      // makes that promise in meta.introBody; an editorial-led quiz has no
      // intro body, so without it the S10 tally and the 8-signs grid pay off
      // a promise that was never made. The final copy keeps the countable
      // promise ("you can't explain away all 8") and adds the reason a reader
      // has never joined the dots himself: he met the signs years apart.
      "Most men meet these one at a time, years apart, and file each one under getting older. **That's the trick of it. One sign is easy to explain away. You can't explain away all 8.**",
      "So let's do this the honest way and count how many you already have. Answer them the way things actually are, not the way you'd say it out loud at the gym.",
    ],
    cta: "Show me how many I have",
  },
  {
    id: "puffy-face",
    type: "single",
    prompt: "When you look in the mirror, does your face look puffier or softer than it used to?",
    options: [
      { value: "yes-noticeably", label: "Yes, noticeably" },
      { value: "some-mornings", label: "Some mornings" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "wake-pattern",
    type: "single",
    prompt: "Do you wake up between 2 and 3 AM, for no reason?",
    options: [
      { value: "most-nights", label: "Most nights" },
      { value: "few-times-week", label: "A few times a week" },
      { value: "rarely", label: "Rarely" },
      { value: "never", label: "Never" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Cortisol runs on a 24-hour rhythm. When it’s stuck high, it spikes in the early-morning hours. That’s the 2–3 AM wake-up.",
    },
  },
  {
    id: "tension",
    type: "single",
    prompt: "Do you carry physical tension like a tight neck, stiff shoulders, clenched jaw?",
    options: [
      { value: "constantly", label: "Constantly" },
      { value: "most-days", label: "By the end of most days" },
      { value: "occasionally", label: "Occasionally" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "energy-3pm",
    type: "single",
    prompt: "How’s your energy at 3pm compared to when you wake up?",
    options: [
      { value: "drained", label: "Completely drained, I need caffeine or a nap" },
      { value: "afternoon-slump", label: "Noticeably lower, afternoon slump hits hard" },
      { value: "slightly-tired", label: "Slightly tired, still functional but slower" },
      { value: "same-energy", label: "Same energy, no afternoon crash" },
    ],
  },
  {
    id: "bridge-absolution",
    type: "editorial",
    eyebrow: "A quick word",
    // Three variants (audit): "You're not imagining it" is a contradiction at
    // zero signs, and "{signCount} of the 8" is broken grammar at one. Only
    // `many` may carry the token -- enforced by the schema.
    //
    // This beat fires after 4 of the 8 signs have been asked (puffy-face,
    // wake-pattern, tension, energy-3pm -- see REPORTED_SIGNS in
    // cortisol-profile.ts), so `signCount` here is 0..4, never 8. The final
    // copy is the first version to SAY that ("only halfway through the list",
    // "the next 4", CTA "Show me the last 4"), which both reads honestly at
    // {signCount}=4 and turns the midpoint into a forward pull instead of a
    // verdict. Do not reword these to imply all eight have been asked.
    heading: "You're not imagining it.",
    body: [
      "You've already flagged **{signCount} of the 8 classic cortisol signs**, and you're only halfway through the list.",
    ],
    reactive: { token: "signCount" },
    variants: {
      // The delivered copy promised "I'd rather tell a man he's fine than
      // sell him something he doesn't need", which the floored verdict then
      // broke four screens later (EARLY DECLINE / "Hormonal Recovery"). Both
      // ends now agree: the zero-signs completer gets the optimization
      // verdict (quizzes/cortisol-story-profile.ts), so this screen can keep
      // the promise and pivot from damage to headroom. If that branch is ever
      // removed, this copy has to come back to "clean so far" -- the "ceiling"
      // line below is a promise the verdict has to pay.
      zero: {
        heading: "Good. You're clean so far.",
        body: [
          "You haven't flagged a single one of the first 4 signs. That's real, and I'd rather say so than talk you into a problem you don't have.",
          "So for you this isn't about damage. It's about headroom. Every man has a testosterone number, and yours is either working for you or leaving something on the table.",
          "Four signs left. Answer them straight and I'll show you where your ceiling is.",
        ],
      },
      one: {
        heading: "You're not imagining it.",
        body: [
          "You've already flagged **one of the 8 classic cortisol signs**, and one is enough to pay attention to, because they rarely travel alone.",
          "That sign isn't laziness and it isn't just getting older, whatever you were told the last time you brought it up. That's what a body does when it's held under pressure it was never built to carry.",
          "The next 4 will tell us whether it's a one-off or a pattern.",
        ],
      },
      many: {
        heading: "You're not imagining it.",
        body: [
          "You've already flagged **{signCount} of the 8 classic cortisol signs**, and you're only halfway through the list.",
          "That's not laziness and it's not just getting older, whatever you were told the last time you brought it up. That's what a body does when it's held under pressure it was never built to carry.",
          "That's a pattern. The next 4 will show what it's already cost you.",
        ],
      },
    },
    // Pays off the eight-signs loop planted on `pivot`. Renders below the CTA
    // (rule 20) as supporting proof.
    image: {
      src: "/static/images/cortisol-signs.webp",
      alt: "Eight common signs of high cortisol: puffy face, waking at 2 to 3 AM, tensed neck, low energy, stubborn belly fat, sugar cravings, brain fog, low libido",
    },
    cta: "Show me the last 4",
  },
  {
    id: "body-composition",
    type: "single",
    prompt: "How would you describe your current body composition?",
    options: [
      { value: "lean-muscular", label: "Lean and muscular" },
      { value: "average", label: "Average build" },
      { value: "soft-some-muscle", label: "Soft with some muscle" },
      { value: "overweight", label: "Overweight / out of shape" },
    ],
  },
  {
    id: "sugar-cravings",
    type: "single",
    prompt: "Do sugar or junk-food cravings take over in the afternoon or evening?",
    options: [
      { value: "daily", label: "Daily" },
      { value: "few-times-week", label: "A few times a week" },
      { value: "occasionally", label: "Occasionally" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "brain-fog",
    type: "single",
    // Em dash retained: this prompt is reused byte-verbatim from the live
    // cortisol quiz. Purging it would diverge the prompt string from the
    // parent for no functional gain (spec §12 records the decision).
    prompt:
      "How often does brain fog hit — losing focus mid-task, walking into a room and forgetting why?",
    options: [
      { value: "daily", label: "Daily" },
      { value: "most-days", label: "Most days" },
      { value: "sometimes", label: "Sometimes" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "libido",
    type: "single",
    prompt: "Has your drive dropped compared to a few years ago?",
    options: [
      { value: "significantly", label: "Significantly" },
      { value: "somewhat", label: "Somewhat" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Testosterone and cortisol compete for real estate in your body. So when cortisol runs high, drive is usually the first thing to fade.",
    },
  },
  {
    id: "when-started",
    type: "single",
    prompt: "When did you start noticing these changes?",
    options: [
      { value: "over-year", label: "Over a year ago" },
      { value: "past-year", label: "In the past year" },
      { value: "few-months", label: "In the past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    id: "bridge-mechanism",
    type: "editorial",
    eyebrow: "What your answers point to",
    heading: "Your testosterone isn't missing. It's being held.",
    // The flow's only ingredient beat, so it carries the canon proof numbers
    // (Tongkat 1,000 mg, Shilajit 400 mg, +37% free T, cortisol down up to
    // 16% -- all docs/product-brief.md). The "held, not missing" reframe is
    // what makes the normal-labs objection planted on S1 pay off here.
    //
    // NOT IN CANON, needs a source before this page takes traffic: "Those
    // studies ran on 200 to 600 mg" (a claim about the Tongkat literature)
    // and "Most bottles don't even reach the bottom of that range" (a claim
    // about competitors). The brief says canon, not the advertorial, and canon
    // says only "most are underdosed" without a range. The footnote below
    // covers the label doses and the two percentages -- it does NOT cover
    // these two sentences.
    body: [
      "Stuck high, cortisol goes after your testosterone two ways at once. First it drives up SHBG, the protein that binds testosterone and locks it where your body can't reach it. Then it packs on the belly fat that converts whatever's left into estrogen. **So your total number can read fine on a lab report while almost none of it ever reaches you.**",
      "Which is why adding a testosterone booster on its own tends to do nothing. You can pour more in, but if the lock is on and the drain is open, you never get to use any of it. The cortisol has to come down **first**. That's the step almost everything on the shelf skips, because lowering cortisol doesn't sell as well as a picture of a bodybuilder.",
      "That's the job Tongkat Ali does. In the research, it's been shown to lower cortisol by as much as 16% and raise free testosterone by 37%.¹ **Those studies ran on 200 to 600 mg. Most bottles don't even reach the bottom of that range. Mars Men runs 1,000.**",
      "Alongside 400 mg of Shilajit, to support the cells that actually make testosterone. Every dose is printed on the label and checked by an outside lab, because a number you can't verify is just marketing.",
    ],
    pullQuote: "Lower the cortisol. Free the testosterone.",
    // The flow's first product mention is in WORDS only; the product VISUAL
    // is deferred to the offer screen so the personalized diagnosis lands
    // first. This carries the mechanism diagram instead (spec §4/§10) -- do
    // not swap in a bottle shot.
    image: {
      src: "/static/images/cortisol-mechanism.webp",
      alt: "How testosterone gets suppressed: the brain signals the testes, but SHBG locks up testosterone and estrogen conversion drains it, leaving less free testosterone in the body",
    },
    // Anchored by the "¹" in body para 3, at the sentence the second half of
    // the note qualifies. Renders last on the screen, below the image.
    footnotes: [
      {
        marker: "1",
        text: "Tongkat Ali 1,000 mg and Shilajit 400 mg, per the Mars Men label. The 16% cortisol and 37% free-testosterone figures come from published Tongkat Ali research, not from a trial of the Mars Men formula.",
      },
    ],
    cta: "Tell me what my answers mean",
  },
  {
    id: "analyzing",
    type: "editorial",
    // Pays off the bridge's pull quote by reusing its held/freed imagery
    // rather than restating the mechanism.
    //
    // The final copy delivered each variant as one line opening "Analyzing
    // your cortisol profile." -- that sentence is the `heading` here, so the
    // rendered text matches the delivered copy without saying it twice. The
    // variants carry only the sentence that follows it. All eight signs have
    // been asked by this screen, so unlike S10 the tally can read 0..8.
    heading: "Analyzing your cortisol profile…",
    body: [
      "Cross-checking the **{signCount} of 8 signs** you flagged, and measuring how hard your cortisol is holding your testosterone down.",
    ],
    reactive: { token: "signCount" },
    variants: {
      zero: {
        body: [
          "Weighing your answers against all **8 signs**, and checking whether cortisol has started holding your testosterone down.",
        ],
      },
      one: {
        body: [
          "Confirming the **one sign** you flagged, and measuring how hard your cortisol is holding your testosterone down.",
        ],
      },
      many: {
        body: [
          "Cross-checking the **{signCount} of 8 signs** you flagged, and measuring how hard your cortisol is holding your testosterone down.",
        ],
      },
    },
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const cortisolStoryQuiz = defineQuiz({
  id: "cortisol-story",
  slug: "cortisol-story",
  meta: {
    title: "The #1 Thing Keeping Men Overweight in 2026",
    introHeadline: "The #1 thing keeping men overweight in 2026 isn't food. It's cortisol.",
    // Editorial-led: the `hook` beat IS the hero, so QuizIntro is suppressed
    // and no testimonial card renders (rule 19 opt-out). introHeadline is
    // still required by the schema and is what the Shopify page title
    // defaults to at publish time.
    editorialLed: true,
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    return QUESTION_ORDER[idx + 1] ?? null;
  },

  // Same 10 inputs as the plain cortisol quiz, so routing is shared rather
  // than cloned -- a second copy would drift the moment either is edited.
  classifier(answers: Answers): PersonaKey {
    return cortisolQuiz.classifier(answers);
  },

  // The shared cortisol verdict, plus the zero-signs optimization exception
  // (quizzes/cortisol-story-profile.ts). Scoped to this slug on purpose: the
  // live `cortisol` quiz and its tracking clones keep the rule-12a floor.
  deriveProfile: deriveCortisolStoryProfile,

  // Feeds `{signCount}` to the two reactive editorial beats. Pure function of
  // answers-so-far, so the mid-flow bridge and the analyzing screen read the
  // same tally the verdict's severity was computed from.
  deriveTokens: (answers: Answers) => ({
    signCount: String(countReportedSigns(answers)),
  }),
});
